import React, { useEffect, useState } from 'react';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { useLocation, useNavigate } from 'react-router-dom';
import '@aws-amplify/ui-react/styles.css';
import './Login.css';
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser, fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';
import { listUserProfiles } from '../../../graphql/queries';
import { updateUserSpecialty } from '../../../api/userProfileApi';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { route } = useAuthenticator();

  const from = location.state?.from || '/';
  const [finishingOAuth, setFinishingOAuth] = useState(false);
  const [oauthError, setOauthError] = useState('');

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  useEffect(() => {
    if (route === 'authenticated') {
      (async () => {
        let next = from;
        try {
          const pending = localStorage.getItem('medmap.pendingSpecialty');
          if (pending) {
            const user = await getCurrentUser();
            const client = generateClient();
            const { data } = await client.graphql({
              query: listUserProfiles,
              variables: { filter: { owner: { eq: user.username } } },
              authMode: 'userPool'
            });
            const profile = data?.listUserProfiles?.items?.[0];

            if (!profile) {
              // No profile yet; continue to account creation and keep pending specialty
              next = '/account_create';
            } else {
              // Update chosen specialty now that we have a profile
              await updateUserSpecialty(profile.id, pending);
              try { localStorage.removeItem('medmap.pendingSpecialty'); } catch {}

              const isNameOk = !!profile.name;
              const isUniOk = !!profile.medicalUniversity;
              const isYearOk = Number(profile.yearInMedSchool) > 0;

              if (isNameOk && isUniOk && isYearOk) {
                // Profile is complete, skip account creation
                next = '/pathfinder-setup';
              } else {
                // After locking in a goal, continue flow to account creation page
                next = '/account_create';
              }
            }
          }
        } catch (e) {
          console.error('Post-login pending specialty sync failed:', e);
        } finally {
          navigate(next, { replace: true });
        }
      })();
    }
  }, [route, navigate, from]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isOAuthCallback = params.has('code') || params.has('state') || params.has('error');

    if (!isOAuthCallback) return;

    let cancelled = false;
    setFinishingOAuth(true);
    setOauthError('');

    (async () => {
      for (let attempt = 1; attempt <= 20; attempt++) {
        try {
          const session = await fetchAuthSession();
          const hasTokens = !!session?.tokens?.idToken;
          if (!hasTokens) throw new Error('Tokens not ready yet');

          // confirms user is actually established in Amplify
          await getCurrentUser();

          if (cancelled) return;

          // IMPORTANT: now it’s safe to navigate away from /login
          navigate(from, { replace: true });
          return;
        } catch (e) {
          if (attempt === 20) {
            if (!cancelled) {
              setOauthError('Could not finish Google sign-in. Please try again.');
              setFinishingOAuth(false);
            }
            return;
          }
          await sleep(250);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [location.search, navigate, from]);

  if (finishingOAuth) {
    return (
      <div className="content">
        <section className="auth-hero-page">
          <div className="auth-hero-inner">
            <div className="auth-card">
              <p>Finishing Google sign-in…</p>
              {oauthError ? <p style={{ color: 'crimson' }}>{oauthError}</p> : null}
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (route === 'authenticated') return null;

  return (
    <div className="content">
      <section className="auth-hero-page">
        <div className="auth-hero-inner">
          <div className="auth-copy">
            <h1 className="auth-title">
              Sign in or <span className="auth-gradient-text">Create your account</span>
            </h1>
            <p className="auth-sub">
              Save your specialty and track with Pathfinder
            </p>
            <ul className="auth-benefits" aria-label="Why join MedMap">
              
              
            </ul>
          </div>

          <div className="auth-card" id="auth">
            <Authenticator>
              {/* keep default UI */}
            </Authenticator>

            <div className="auth-divider">
              <span>or</span>
            </div>

            <button
              className="google-signin-btn"
              onClick={() => signInWithRedirect({ provider: 'Google' })}
            >
              <img
                src="/GoogleLogo.png"
                alt=""
                className="google-icon"
              />
              <span>Continue with Google</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;




